/**
 * LaunchResetPanel — Admin Tools surface for the one-shot pre-launch
 * operational reset (`POST /api/admin/launch-reset`).
 *
 * Purpose: let an operator review the DRY-RUN result (what will be wiped vs
 * preserved, which accounts survive) through the UI instead of curl, and —
 * behind a strong typed-token + confirm guard — EXECUTE the reset.
 *
 * Safety contract (mirrors the backend):
 *   - Dry-run is non-destructive; we auto-send the sentinel token for it.
 *   - Execute requires the operator to TYPE the exact sentinel
 *     ("RESET_PRODUCTION_NOW") into the danger-zone field AND confirm a
 *     window.confirm dialog. The backend independently re-validates the
 *     token, validates keeper emails exist, and snapshots every affected
 *     collection to `__backup_<ts>_*` BEFORE deleting anything.
 *   - User deletion is OPT-IN (`wipe_users`, default off → all users kept).
 */
import React, { useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import {
  Rocket, ShieldAlert, Database, Trash2, CheckCircle2, XCircle,
  Loader2, AlertTriangle, Users,
} from "lucide-react";

const SENTINEL = "RESET_PRODUCTION_NOW";

const StatTile = ({ label, value, tone = "neutral", testid }) => {
  const tones = {
    neutral: "bg-white border-[#E5E7EB] text-[#111]",
    danger: "bg-[#FEF2F2] border-[#FCA5A5] text-[#7F1D1D]",
    safe: "bg-[#F0FDF4] border-[#86EFAC] text-[#14532D]",
  };
  return (
    <div className={`rounded-xl border-2 p-4 ${tones[tone]}`} data-testid={testid}>
      <div className="text-2xl font-heading font-black leading-none">{value}</div>
      <div className="text-xs mt-1 uppercase tracking-wide opacity-80">{label}</div>
    </div>
  );
};

export function LaunchResetPanel() {
  const { user } = useAuth();
  const [keeperEmails, setKeeperEmails] = useState(user?.email || "");
  const [wipeUsers, setWipeUsers] = useState(false);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [dryRun, setDryRun] = useState(null);
  const [execResult, setExecResult] = useState(null);
  const [typedToken, setTypedToken] = useState("");

  const keeperList = () =>
    keeperEmails.split(",").map((e) => e.trim().toLowerCase()).filter(Boolean);

  const runDryRun = async () => {
    const keepers = keeperList();
    if (!keepers.length) {
      toast.error("Enter at least one keeper email (the admin running this).");
      return;
    }
    setLoading(true);
    setExecResult(null);
    try {
      const { data } = await api.post("/admin/launch-reset", {
        confirmation_token: SENTINEL,
        keeper_emails: keepers,
        dry_run: true,
        wipe_users: wipeUsers,
      });
      setDryRun(data);
      toast.success("Dry-run complete — review the report below.");
    } catch (e) {
      setDryRun(null);
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const runExecute = async () => {
    if (typedToken !== SENTINEL) {
      toast.error(`Type "${SENTINEL}" exactly to unlock execution.`);
      return;
    }
    const keepers = keeperList();
    if (!keepers.length) {
      toast.error("Enter at least one keeper email.");
      return;
    }
    const warn = wipeUsers
      ? "This will PERMANENTLY DELETE all users except the keeper emails, plus ALL operational data (rides, payments, events). A backup is taken first. Continue?"
      : "This will PERMANENTLY DELETE all operational data (rides, payments, events). Users are preserved. A backup is taken first. Continue?";
    if (!window.confirm(warn)) return;
    setExecuting(true);
    try {
      const { data } = await api.post("/admin/launch-reset", {
        confirmation_token: typedToken,
        keeper_emails: keepers,
        dry_run: false,
        wipe_users: wipeUsers,
      });
      setExecResult(data);
      setDryRun(null);
      setTypedToken("");
      toast.success("Launch reset executed. Backup created.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExecuting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 space-y-6" data-testid="launch-reset-panel">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-[#111] flex items-center justify-center flex-shrink-0">
          <Rocket className="w-5 h-5 text-[#FACC15]" />
        </div>
        <div>
          <h2 className="font-heading font-black text-2xl text-[#111] leading-tight">
            Launch Reset
          </h2>
          <p className="text-sm text-[#6B7280] mt-1 max-w-2xl">
            One-shot pre-launch cleanup. Wipes operational data (rides, payments,
            events) so you onboard your first operator on a clean slate. Platform
            configuration and (by default) all user accounts are preserved. Always
            run a <strong>dry-run</strong> first.
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className="rounded-2xl border-2 border-[#E5E7EB] bg-white p-5 space-y-4">
        <div>
          <label className="block text-xs font-heading font-black uppercase tracking-wide text-[#374151] mb-1.5">
            Keeper emails (comma-separated)
          </label>
          <input
            type="text"
            value={keeperEmails}
            onChange={(e) => setKeeperEmails(e.target.value)}
            placeholder="admin@yourcompany.com"
            className="w-full rounded-lg border-2 border-[#E5E7EB] px-3 py-2 text-sm focus:border-[#111] outline-none"
            data-testid="launch-reset-keeper-input"
          />
          <p className="text-xs text-[#6B7280] mt-1">
            Must include the admin running this. Validated server-side before anything happens.
          </p>
        </div>

        <label className="flex items-start gap-2.5 cursor-pointer" data-testid="launch-reset-wipe-users-label">
          <input
            type="checkbox"
            checked={wipeUsers}
            onChange={(e) => setWipeUsers(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-[#DC2626]"
            data-testid="launch-reset-wipe-users-checkbox"
          />
          <span className="text-sm text-[#374151]">
            <span className="font-bold text-[#B91C1C]">Also delete users</span> not in the keeper
            list (drivers + passengers + other admins). Leave OFF to preserve every account —
            recommended unless you are clearing test accounts.
          </span>
        </label>

        <div className="flex flex-wrap gap-3 pt-1">
          <Button
            onClick={runDryRun}
            disabled={loading}
            className="bg-[#111] text-white font-heading font-black h-11 px-6 rounded-xl hover:bg-[#222]"
            data-testid="launch-reset-dry-run-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldAlert className="w-4 h-4 mr-2" />}
            {loading ? "Running…" : "Run Dry Run"}
          </Button>
        </div>
      </div>

      {/* Dry-run report */}
      {dryRun && (
        <div className="rounded-2xl border-2 border-[#111] bg-white overflow-hidden" data-testid="launch-reset-dry-run-result">
          <div className="bg-[#111] text-white px-5 py-3 flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-[#FACC15]" />
            <span className="font-heading font-black text-sm uppercase tracking-wide">
              Dry-Run Preview · no data has been changed
            </span>
          </div>

          <div className="p-5 space-y-5">
            {/* Headline tiles */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatTile label="Total users now" value={dryRun.before?.users_total ?? "—"} testid="lr-tile-users" />
              <StatTile
                label="Users deleted"
                value={dryRun.would_delete_user_count ?? 0}
                tone={dryRun.would_delete_user_count ? "danger" : "safe"}
                testid="lr-tile-users-deleted"
              />
              <StatTile label="Rides now" value={dryRun.before?.rides ?? "—"} testid="lr-tile-rides" />
              <StatTile
                label="Admin login survives"
                value={dryRun.projected_after?.admin_login_still_works ? "YES" : "NO"}
                tone={dryRun.projected_after?.admin_login_still_works ? "safe" : "danger"}
                testid="lr-tile-admin-survives"
              />
            </div>

            {/* Users action */}
            <div className="rounded-xl border-2 border-[#E5E7EB] p-4">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-4 h-4 text-[#374151]" />
                <span className="font-heading font-black text-sm uppercase tracking-wide text-[#374151]">Users</span>
              </div>
              <p className="text-sm text-[#111]">
                Action: <span className="font-bold">{dryRun.users_action}</span>
              </p>
              <p className="text-sm text-[#6B7280] mt-1">
                Kept: {(dryRun.would_keep_users || []).join(", ") || "—"}
              </p>
            </div>

            {/* Will delete */}
            <div className="rounded-xl border-2 border-[#FCA5A5] bg-[#FEF2F2] p-4">
              <div className="flex items-center gap-2 mb-3">
                <Trash2 className="w-4 h-4 text-[#B91C1C]" />
                <span className="font-heading font-black text-sm uppercase tracking-wide text-[#7F1D1D]">
                  Will be deleted
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {Object.entries(dryRun.would_wipe_collections || {}).map(([k, v]) => (
                  <div key={k} className="bg-white rounded-lg border border-[#FCA5A5] px-3 py-2" data-testid={`lr-wipe-${k}`}>
                    <div className="text-lg font-heading font-black text-[#7F1D1D]">{v}</div>
                    <div className="text-[11px] text-[#7F1D1D] opacity-80">{k}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Will preserve */}
            <div className="rounded-xl border-2 border-[#86EFAC] bg-[#F0FDF4] p-4">
              <div className="flex items-center gap-2 mb-2">
                <Database className="w-4 h-4 text-[#15803D]" />
                <span className="font-heading font-black text-sm uppercase tracking-wide text-[#14532D]">
                  Preserved (never touched)
                </span>
              </div>
              <p className="text-sm text-[#14532D]">
                {(dryRun.would_preserve_collections || []).join(" · ")}
                {!wipeUsers && " · all user accounts"}
              </p>
            </div>

            {/* Notes */}
            {dryRun.notes?.length > 0 && (
              <ul className="text-xs text-[#6B7280] space-y-1 list-disc pl-5">
                {dryRun.notes.map((n, i) => <li key={i}>{n}</li>)}
              </ul>
            )}
          </div>

          {/* Danger zone — execute */}
          <div className="border-t-2 border-[#FCA5A5] bg-[#FEF2F2] p-5">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-[#B91C1C]" />
              <span className="font-heading font-black text-sm uppercase tracking-wide text-[#7F1D1D]">
                Danger zone · execute reset
              </span>
            </div>
            <p className="text-xs text-[#7F1D1D] mb-3">
              Irreversible (a backup is taken first). To unlock, type
              <code className="mx-1 px-1.5 py-0.5 bg-white rounded border border-[#FCA5A5] font-mono">{SENTINEL}</code>
              exactly.
            </p>
            <div className="flex flex-wrap gap-3 items-center">
              <input
                type="text"
                value={typedToken}
                onChange={(e) => setTypedToken(e.target.value)}
                placeholder={SENTINEL}
                className="flex-1 min-w-[220px] rounded-lg border-2 border-[#FCA5A5] px-3 py-2 text-sm font-mono focus:border-[#B91C1C] outline-none"
                data-testid="launch-reset-token-input"
              />
              <Button
                onClick={runExecute}
                disabled={executing || typedToken !== SENTINEL}
                className="bg-[#DC2626] text-white font-heading font-black h-11 px-6 rounded-xl hover:bg-[#B91C1C] disabled:opacity-40"
                data-testid="launch-reset-execute-btn"
              >
                {executing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                {executing ? "Executing…" : "Execute Reset"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Execute result */}
      {execResult && (
        <div className="rounded-2xl border-2 border-[#15803D] bg-white overflow-hidden" data-testid="launch-reset-exec-result">
          <div className="bg-[#15803D] text-white px-5 py-3 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            <span className="font-heading font-black text-sm uppercase tracking-wide">Reset executed</span>
          </div>
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatTile label="Users remaining" value={execResult.after?.user_count ?? "—"} tone="safe" testid="lr-exec-users" />
              <StatTile label="Admins remaining" value={execResult.after?.admin_count ?? "—"} tone="safe" testid="lr-exec-admins" />
              <StatTile label="Rides remaining" value={execResult.after?.ride_count ?? 0} tone="safe" testid="lr-exec-rides" />
              <StatTile label="Backup created" value={execResult.backup_created ? "YES" : "NO"} tone="safe" testid="lr-exec-backup" />
            </div>
            <div className="rounded-xl border-2 border-[#E5E7EB] p-4">
              <p className="text-sm text-[#111]">
                Backup prefix: <code className="font-mono text-xs bg-[#F3F4F6] px-1.5 py-0.5 rounded">{execResult.backup_prefix}</code>
              </p>
              <p className="text-xs text-[#6B7280] mt-1">
                Every affected collection was snapshotted to <code>{execResult.backup_prefix}&lt;collection&gt;</code> before deletion.
              </p>
            </div>
            <div className="rounded-xl border-2 border-[#E5E7EB] p-4">
              <div className="font-heading font-black text-xs uppercase tracking-wide text-[#374151] mb-2">Remaining users</div>
              <div className="space-y-1">
                {(execResult.remaining_users || []).map((u) => (
                  <div key={u.id} className="text-sm text-[#111] flex items-center gap-2">
                    <span className="font-mono text-xs px-1.5 py-0.5 bg-[#F3F4F6] rounded uppercase">{u.role}</span>
                    {u.email}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {!dryRun && !execResult && (
        <div className="text-center text-sm text-[#9CA3AF] py-6 flex flex-col items-center gap-2" data-testid="launch-reset-empty">
          <XCircle className="w-6 h-6 opacity-40" />
          Run a dry-run to preview exactly what will be deleted and preserved.
        </div>
      )}
    </div>
  );
}

export default LaunchResetPanel;
